// src/Pages/TodoTemplates.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api';

// Icons
import AIcon from '../assets/icons/A.svg';
import CIcon from '../assets/icons/C.svg';
import DCIcon from '../assets/icons/DC.svg';
import DocIcon from '../assets/icons/DocIcon.svg';
import PIcon from '../assets/icons/P.svg';
import PencilTemplateIcon from '../assets/icons/PenciTemplate.svg';

// Map filename => imported URL
const iconMap = {
  'A.svg': AIcon,
  'C.svg': CIcon,
  'DC.svg': DCIcon,
  'DocIcon.svg': DocIcon,
  'P.svg': PIcon,
  'PenciTemplate.svg': PencilTemplateIcon
};

// Icons the user can choose from
const availableIcons = [
  { name: 'C', file: 'C.svg' },
  { name: 'DC', file: 'DC.svg' },
  { name: 'P', file: 'P.svg' },
  { name: 'A', file: 'A.svg' },
  { name: 'Doc Icon', file: 'DocIcon.svg' },
  { name: 'PencilTemplate', file: 'PenciTemplate.svg' }
];

const TodoTemplates = () => {
  const [templates, setTemplates] = useState([]);

  // Modal states (create/update)
  const [showModal, setShowModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [newTemplate, setNewTemplate] = useState({ title: '', icon: '' });

  // Delete Confirm
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);

  // Duplicate Modal
  const [duplicateModalOpen, setDuplicateModalOpen] = useState(false);
  const [duplicatingTemplate, setDuplicatingTemplate] = useState(null);
  const [duplicateTemplateData, setDuplicateTemplateData] = useState({
    title: '',
    icon: ''
  });

  // =============================
  // 1) Fetch all templates on mount
  // =============================
  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const res = await api('/api/todotemplates');
      if (!res.ok) {
        throw new Error(`Failed to fetch templates. Status: ${res.status}`);
      }
      const data = await res.json(); // array of { _id, title, icon, categories, tasks, ... }
      setTemplates(data);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  // =============================
  // 2) Create a new Template
  // =============================
  const handleAddTemplate = () => {
    setEditingTemplate(null);
    setNewTemplate({ title: '', icon: '' });
    setShowModal(true);
  };

  const handleSaveTemplate = async () => {
    if (!newTemplate.title.trim()) {
      alert('Template title is required');
      return;
    }
    try {
      // POST => /api/todotemplates
      const res = await api('/api/todotemplates', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newTemplate)
      });
      if (!res.ok) {
        throw new Error(`Failed to create template. Status: ${res.status}`);
      }
      const created = await res.json();
      setTemplates((prev) => [...prev, created]);
      setShowModal(false);
      setNewTemplate({ title: '', icon: '' });
    } catch (error) {
      console.error('Create error:', error);
    }
  };

  // =============================
  // 3) Edit / Update an existing Template
  // =============================
  const handleEditTemplate = (template) => {
    setEditingTemplate(template);
    setNewTemplate({ title: template.title, icon: template.icon || '' });
    setShowModal(true);
  };

  const handleUpdateTemplate = async () => {
    if (!newTemplate.title.trim()) {
      alert('Template title is required.');
      return;
    }
    if (!editingTemplate) return;

    try {
      // PUT => /api/todotemplates/:id
      const res = await api(`/api/todotemplates/${editingTemplate._id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newTemplate)
      });
      if (!res.ok) {
        throw new Error(`Failed to update template. Status: ${res.status}`);
      }
      const updated = await res.json();
      setTemplates((prev) => prev.map((t) => (t._id === updated._id ? updated : t)));
      setShowModal(false);
      setEditingTemplate(null);
      setNewTemplate({ title: '', icon: '' });
    } catch (error) {
      console.error('Update error:', error);
    }
  };

  // =============================
  // 4) Delete Template (Confirm)
  // =============================
  const confirmDeleteTemplate = (template) => {
    setTemplateToDelete(template);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (!templateToDelete) return;
    try {
      // DELETE => /api/todotemplates/:id
      const res = await api(`/api/todotemplates/${templateToDelete._id}`, {
        method: 'DELETE'
      });
      if (!res.ok) {
        throw new Error(`Delete error: status ${res.status}`);
      }
      setTemplates((prev) => prev.filter((t) => t._id !== templateToDelete._id));
      setShowDeleteConfirm(false);
      setTemplateToDelete(null);

      // Also close the edit modal if open
      setShowModal(false);
    } catch (error) {
      console.error('Delete error:', error);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
    setTemplateToDelete(null);
  };

  // =============================
  // 5) Duplicate Feature (now clones subdocs too!)
  // =============================
  const handleDuplicateClick = (template) => {
    setDuplicatingTemplate(template);
    setDuplicateTemplateData({
      title: template.title + ' Duplicate',
      icon: template.icon || ''
    });
    setDuplicateModalOpen(true);
  };

  const handleDuplicateInputChange = (e) => {
    const { name, value } = e.target;
    setDuplicateTemplateData((prev) => ({ ...prev, [name]: value }));
  };

  const handleDuplicateIconSelect = (fileName) => {
    setDuplicateTemplateData((prev) => ({ ...prev, icon: fileName }));
  };

  /**
   * confirmDuplicate:
   * 1) Fetch the original doc (including categories/tasks).
   * 2) Assign new string IDs for all categories/tasks.
   * 3) Map old ID => new ID in tasks for categoryId, dependencyTaskId references.
   * 4) POST the new doc with cloned subdocs to /api/todotemplates
   * 5) Insert into local state.
   */
  const confirmDuplicate = async () => {
    if (!duplicateTemplateData.title.trim()) {
      alert('Template title is required for duplication.');
      return;
    }

    try {
      // 1) Fetch the original template to get categories[] and tasks[]
      const originalRes = await api(`/api/todotemplates/${duplicatingTemplate._id}`);
      if (!originalRes.ok) {
        throw new Error(
          `Failed to fetch original template for duplication. Status: ${originalRes.status}`
        );
      }
      const originalDoc = await originalRes.json();
      // originalDoc = { _id, title, icon, categories: [...], tasks: [...], etc. }

      // 2) Build a map oldId => newId
      const oldIdToNewId = {};

      // 2a) Clone categories
      const clonedCategories = (originalDoc.categories || []).map((cat) => {
        const newId = crypto.randomUUID();
        oldIdToNewId[cat._id] = newId;
        return {
          ...cat,
          _id: newId
        };
      });

      // 2b) Clone tasks (first pass: new _id)
      let clonedTasks = (originalDoc.tasks || []).map((task) => {
        const newId = crypto.randomUUID();
        oldIdToNewId[task._id] = newId;
        return {
          ...task,
          _id: newId
          // We'll fix references below
        };
      });

      // 2c) Second pass: fix categoryId & dependencyTaskId
      clonedTasks = clonedTasks.map((task) => {
        // If the old categoryId is in our map, re-map it
        let newCategoryId = task.categoryId;
        if (oldIdToNewId[newCategoryId]) {
          newCategoryId = oldIdToNewId[newCategoryId];
        }

        let newDependencyId = task.dependencyTaskId;
        if (newDependencyId && oldIdToNewId[newDependencyId]) {
          newDependencyId = oldIdToNewId[newDependencyId];
        }

        return {
          ...task,
          categoryId: newCategoryId,
          dependencyTaskId: newDependencyId || null
        };
      });

      // 3) Construct the new template doc to POST
      const newBody = {
        title: duplicateTemplateData.title, // user-chosen name
        icon: duplicateTemplateData.icon,
        categories: clonedCategories,
        tasks: clonedTasks
      };

      // 4) POST => /api/todotemplates to create brand-new doc
      const createRes = await api('/api/todotemplates', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newBody)
      });
      if (!createRes.ok) {
        throw new Error(`Failed to create duplicated template. Status: ${createRes.status}`);
      }
      const createdDup = await createRes.json();

      // 5) Insert into local state
      setTemplates((prev) => [...prev, createdDup]);

      // 6) Close modal
      setDuplicateModalOpen(false);
      setDuplicatingTemplate(null);
      setDuplicateTemplateData({ title: '', icon: '' });
    } catch (error) {
      console.error('Duplicate error:', error);
      alert(`Failed to duplicate: ${error.message}`);
    }
  };

  const cancelDuplicate = () => {
    setDuplicateModalOpen(false);
    setDuplicatingTemplate(null);
    setDuplicateTemplateData({ title: '', icon: '' });
  };

  // =============================
  // Input & Icon changes (for add/edit)
  // =============================
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewTemplate((prev) => ({ ...prev, [name]: value }));
  };

  const handleIconSelect = (fileName) => {
    setNewTemplate((prev) => ({ ...prev, icon: fileName }));
  };

  // =============================
  // Render
  // =============================
  return (
    <div style={{ padding: '20px' }}>
      <h1 className="header" style={{ marginTop: '10px', marginBottom: '30px' }}>
        ToDo Templates
      </h1>

      {/* Create New Template Button */}
      <button
        onClick={handleAddTemplate}
        style={{
          backgroundColor: '#1e1e1e',
          color: 'white',
          border: 'none',
          padding: '15px 30px',
          borderRadius: '8px',
          cursor: 'pointer',
          marginBottom: '40px',
          display: 'flex',
          alignItems: 'center',
          fontWeight: 'bold'
        }}
      >
        <span style={{ fontSize: '24px', marginRight: '10px' }}>+</span>
        Create New Template
      </button>

      {/* ===================== ADD/EDIT MODAL ===================== */}
      {showModal && (
        <div className="modal">
          <div
            className="modal-content"
            style={{
              width: '400px',
              padding: '20px',
              backgroundColor: '#1e1e1e',
              color: 'white',
              borderRadius: '10px',
              margin: 'auto'
            }}
          >
            <h2>{editingTemplate ? 'Edit Template' : 'Add New ToDo Template'}</h2>

            {/* Template Title */}
            <input
              type="text"
              name="title"
              placeholder="Template Name"
              value={newTemplate.title}
              onChange={handleInputChange}
              style={{
                width: 'calc(100% - 20px)',
                marginBottom: '10px',
                padding: '10px',
                backgroundColor: '#333',
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                fontFamily: 'inherit'
              }}
            />

            {/* Icon Picker */}
            <div style={{ marginBottom: '10px' }}>
              <p style={{ margin: '5px 0' }}>Pick an icon:</p>
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '10px',
                  backgroundColor: '#222',
                  padding: '10px',
                  borderRadius: '5px'
                }}
              >
                {availableIcons.map((iconObj) => {
                  const isSelected = newTemplate.icon === iconObj.file;
                  const importedIcon = iconMap[iconObj.file];
                  return (
                    <div
                      key={iconObj.file}
                      onClick={() => handleIconSelect(iconObj.file)}
                      style={{
                        width: '50px',
                        height: '50px',
                        borderRadius: '5px',
                        cursor: 'pointer',
                        backgroundColor: isSelected ? '#555' : 'transparent',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <img
                        src={importedIcon}
                        alt={iconObj.name}
                        style={{ width: '30px', height: '30px' }}
                      />
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Save / Cancel row */}
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <button
                onClick={editingTemplate ? handleUpdateTemplate : handleSaveTemplate}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  fontFamily: 'inherit'
                }}
              >
                {editingTemplate ? 'Update' : 'Save'}
              </button>
              <button
                onClick={() => setShowModal(false)}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#555',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  fontFamily: 'inherit'
                }}
              >
                Cancel
              </button>
            </div>

            {/* "Delete Template" if editing existing */}
            {editingTemplate && (
              <button
                onClick={() => confirmDeleteTemplate(editingTemplate)}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#d9534f',
                  color: 'white',
                  borderRadius: '5px',
                  border: 'none',
                  marginTop: '10px',
                  width: '100%'
                }}
              >
                Delete Template
              </button>
            )}
          </div>
        </div>
      )}

      {/* ===================== DELETE CONFIRM MODAL ===================== */}
      {showDeleteConfirm && templateToDelete && (
        <div className="modal">
          <div
            className="modal-content"
            style={{
              width: '400px',
              padding: '20px',
              backgroundColor: '#1e1e1e',
              color: 'white',
              borderRadius: '10px',
              margin: 'auto'
            }}
          >
            <h2>Confirm Deletion</h2>
            <p>Are you sure you want to delete template "{templateToDelete.title}"?</p>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <button
                onClick={handleConfirmDelete}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#d9534f',
                  color: 'white',
                  borderRadius: '5px',
                  border: 'none'
                }}
              >
                Delete
              </button>
              <button
                onClick={handleCancelDelete}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#555',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===================== DUPLICATE MODAL ===================== */}
      {duplicateModalOpen && duplicatingTemplate && (
        <div className="modal">
          <div
            className="modal-content"
            style={{
              width: '400px',
              padding: '20px',
              backgroundColor: '#1e1e1e',
              color: 'white',
              borderRadius: '10px',
              margin: 'auto'
            }}
          >
            <h2>Duplicate Template</h2>
            <input
              type="text"
              name="title"
              placeholder="Template Name"
              value={duplicateTemplateData.title}
              onChange={handleDuplicateInputChange}
              style={{
                width: 'calc(100% - 20px)',
                marginBottom: '10px',
                padding: '10px',
                backgroundColor: '#333',
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                fontFamily: 'inherit'
              }}
            />

            <p style={{ margin: '5px 0' }}>Pick an icon:</p>
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '10px',
                backgroundColor: '#222',
                padding: '10px',
                borderRadius: '5px'
              }}
            >
              {availableIcons.map((iconObj) => {
                const isSelected = duplicateTemplateData.icon === iconObj.file;
                const importedIcon = iconMap[iconObj.file];
                return (
                  <div
                    key={iconObj.file}
                    onClick={() => handleDuplicateIconSelect(iconObj.file)}
                    style={{
                      width: '50px',
                      height: '50px',
                      borderRadius: '5px',
                      cursor: 'pointer',
                      backgroundColor: isSelected ? '#555' : 'transparent',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <img
                      src={importedIcon}
                      alt={iconObj.name}
                      style={{ width: '30px', height: '30px' }}
                    />
                  </div>
                );
              })}
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '20px' }}>
              <button
                onClick={confirmDuplicate}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#555',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  fontFamily: 'inherit'
                }}
              >
                Duplicate
              </button>
              <button
                onClick={cancelDuplicate}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#555',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  fontFamily: 'inherit'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===================== TEMPLATES GRID ===================== */}
      <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
        {templates.map((template) => {
          // template is { _id, title, icon, categories?: [...], tasks?: [...] }
          const iconImport = template.icon ? iconMap[template.icon] : null;

          return (
            <div
              key={template._id}
              style={{
                width: '240px',
                backgroundColor: '#1e1e1e',
                padding: '20px',
                borderRadius: '10px',
                color: 'white'
              }}
            >
              <Link
                to={`/todo-template/${template._id}`}
                style={{
                  textDecoration: 'none',
                  color: 'inherit'
                }}
              >
                {iconImport ? (
                  <img
                    src={iconImport}
                    alt="Template Icon"
                    style={{
                      width: '60px',
                      height: '60px',
                      objectFit: 'contain',
                      marginBottom: '10px'
                    }}
                  />
                ) : (
                  <div
                    style={{
                      fontSize: '50px',
                      marginBottom: '10px'
                    }}
                  >
                    📄
                  </div>
                )}
                <h3 style={{ margin: '0 0 10px 0', fontSize: '1.2rem' }}>
                  {template.title}
                </h3>
              </Link>

              {/* Row of icons for Edit / Duplicate */}
              <div style={{ display: 'flex', gap: '10px' }}>
                {/* EDIT button */}
                <svg
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleEditTemplate(template);
                  }}
                  width="20"
                  height="20"
                  style={{
                    cursor: 'pointer',
                    fill: '#333333'
                  }}
                  viewBox="0 0 24 24"
                >
                  <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM21 7.04a1.2 1.2 0 0 0 0-1.68l-2.34-2.34a1.2 1.2 0 0 0-1.68 0l-1.84 1.84 3.75 3.75L21 7.04z" />
                </svg>

                {/* DUPLICATE button */}
                <svg
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleDuplicateClick(template);
                  }}
                  width="20"
                  height="20"
                  style={{
                    cursor: 'pointer',
                    fill: '#333333'
                  }}
                  viewBox="0 0 24 24"
                >
                  <path d="M17 1H5c-1.1 0-2 .9-2 2v2h2V3h12v12h-2v2h2c1.1 0 2-.9 2-2V3c0-1.1-.9-2-2-2zm-4 4H3c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h10c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 14H3V7h10v12z" />
                </svg>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default TodoTemplates;